import { campaignPackageReviewSchema } from '@borradh-workspace/contracts/claire';
import type { UIMessage } from 'ai';
import {
  type ToolPartData,
  asToolPart,
  getToolName,
  hasToolResultFailure,
} from './tool-parts';

const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
const nonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

function adIds(value: unknown): Set<string> | undefined {
  if (
    !Array.isArray(value) ||
    value.length < 1 ||
    value.length > 10 ||
    !value.every(nonEmptyString) ||
    new Set(value).size !== value.length
  )
    return;
  return new Set(value);
}

function sameIds(left: Set<string>, right: Set<string> | undefined) {
  return right?.size === left.size && [...left].every((id) => right.has(id));
}

function isReadyPackage(tool: ToolPartData, ids: Set<string>) {
  if (
    tool.state !== 'output-available' ||
    hasToolResultFailure(tool) ||
    tool.errorText ||
    !sameIds(ids, adIds(tool.input?.adIds))
  )
    return false;
  const output = tool.output;
  const card = record(output?.presentation);
  if (card?.type === 'confirmation_required') {
    const review = campaignPackageReviewSchema.safeParse(
      record(card.summary)?.campaignPackage
    );
    return (
      card.action === 'launch_campaign_package' &&
      card.executeToolName === 'meta_ads_launchCampaignPackage' &&
      card.resourceId === tool.input.campaignId &&
      nonEmptyString(card.token) &&
      review.success &&
      sameIds(ids, adIds(review.data.ads.map((ad) => ad.adId)))
    );
  }
  // A completed read/launch must account for every requested ad, including
  // provider identities and offer publication. Partial progress is not recovery.
  const ads = output?.ads;
  return (
    output?.status === 'completed' &&
    output.campaignId === tool.input.campaignId &&
    ['none', 'active'].includes(String(output.offerState)) &&
    sameIds(ids, adIds(output.completedAdIds)) &&
    Array.isArray(output.failedAdIds) &&
    output.failedAdIds.length === 0 &&
    Array.isArray(output.unattemptedAdIds) &&
    output.unattemptedAdIds.length === 0 &&
    Array.isArray(ads) &&
    sameIds(ids, adIds(ads.map((ad) => record(ad)?.adId))) &&
    ads.every((ad) => {
      const progress = record(ad);
      return (
        nonEmptyString(progress?.metaAdId) &&
        ['live', 'pending_review'].includes(String(progress?.state))
      );
    })
  );
}

function isIndividualProposal(
  tool: ToolPartData,
  adId: string,
  campaignId: string
) {
  if (
    tool.state !== 'output-available' ||
    hasToolResultFailure(tool) ||
    tool.errorText
  )
    return false;
  const output = tool.output;
  if (
    (tool.input?.metaCampaignId !== undefined &&
      tool.input.metaCampaignId !== campaignId) ||
    (output?.metaCampaignId !== undefined &&
      output.metaCampaignId !== campaignId)
  )
    return false;
  const card = record(output?.presentation);
  if (getToolName(tool) === 'meta_ads_createDraftAd')
    return (
      output?.adId === adId &&
      output.metaCampaignId === campaignId &&
      output.status === 'draft' &&
      nonEmptyString(output.confirmationToken) &&
      card?.type === 'ad_preview' &&
      record(card.preview)?.variant === 'draft'
    );
  const summary = record(card?.summary);
  return (
    card?.type === 'confirmation_required' &&
    card.action === 'launch_ad' &&
    card.executeToolName === 'meta_ads_executeLaunchAd' &&
    card.resourceId === adId &&
    nonEmptyString(card.token) &&
    typeof summary?.title === 'string' &&
    Array.isArray(summary.fields) &&
    summary.fields.every((field) => {
      const value = record(field);
      return (
        typeof value?.label === 'string' && typeof value.value === 'string'
      );
    })
  );
}

/** Suppress only a recovered preparation notice, keeping the failed call in
 * the original message and its tool trace. Never bridge another user turn. */
export function getRecoveredPackageErrorToolCallIds(
  parts: UIMessage['parts']
): Set<string> {
  const recovered = new Set<string>();
  for (const [index, part] of parts.entries()) {
    const failed = asToolPart(part);
    const ids = adIds(failed?.input?.adIds);
    const campaignId = failed?.input?.campaignId;
    if (
      !failed ||
      getToolName(failed) !== 'meta_ads_launchCampaignPackage' ||
      failed.state !== 'output-available' ||
      !hasToolResultFailure(failed) ||
      failed.input?.confirmationToken !== undefined ||
      failed.output?.status !== undefined ||
      failed.output?.ads !== undefined ||
      failed.output?.completedAdIds !== undefined ||
      !nonEmptyString(campaignId) ||
      !ids
    )
      continue;
    const ready = new Set<string>();
    for (const later of parts.slice(index + 1)) {
      const tool = asToolPart(later);
      if (!tool) continue;
      const name = getToolName(tool);
      if (
        ['meta_ads_updateBudget', 'meta_ads_updateCampaign'].includes(name) &&
        tool.input?.metaCampaignId === campaignId
      ) {
        ready.clear();
        continue;
      }
      if (name === 'meta_ads_launchCampaignPackage') {
        const laterIds = adIds(tool.input?.adIds);
        if (tool.input?.campaignId !== campaignId) continue;
        if (!laterIds) {
          ready.clear();
          continue;
        }
        if (![...laterIds].some((id) => ids.has(id))) continue;
        // The newest overlapping package attempt owns the review. An older
        // ready card cannot conceal a later pending or unsuccessful attempt.
        ready.clear();
        if (isReadyPackage(tool, ids)) for (const id of ids) ready.add(id);
        continue;
      }
      const id =
        name === 'meta_ads_createDraftAd'
          ? tool.output?.adId
          : tool.input?.adId;
      if (!nonEmptyString(id) || !ids.has(id)) continue;
      if (
        [
          'meta_ads_createDraftAd',
          'meta_ads_executeLaunchAd',
          'meta_ads_confirmLaunchAd',
        ].includes(name)
      ) {
        ready.delete(id);
        if (isIndividualProposal(tool, id, campaignId)) ready.add(id);
      } else if (
        [
          'meta_ads_updateAd',
          'meta_ads_replaceAdCreative',
          'meta_ads_deleteDraftAd',
        ].includes(name)
      ) {
        // An edit invalidates the review; require a fresh card for this ad.
        ready.delete(id);
      }
    }
    if (sameIds(ids, ready)) recovered.add(failed.toolCallId);
  }
  return recovered;
}
